import { Component, Inject, OnInit } from '@angular/core'
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { ConfirmDialogService, Rental, selectRentalById, Toaster } from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { AibrnbListingDetails, ConnectionAirbnbService } from '@tv3/store/connection/connection-airbnb.service'
import { Connection, getRealChannelId } from '@tv3/store/connection/connection.model'
import { selectChannelById } from '@tokeet-frontend/tv3-platform'
import { filter, map, startWith, switchMap, take, tap } from 'rxjs/operators'
import { SaveForm } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { ActionFailed, integer } from '@tokeet-frontend/tv3-platform'
import {
  AirBnBPriceSettingsResponse,
  AirBnBUpdatePriceSettingsPayload,
  AirbnbPricingStandardFee,
} from '@tv3/store/connection/connection.types'
import { combineLatest } from 'rxjs'
import {
  AirbnbChannelService,
  AirbnbListingPromotionProgramStatus,
  AirbnbPassThroughOccupancyTax,
  AirbnbPricingRule,
  AirbnbPricingRuleType,
  AirbnbPromotionStatus,
  AirbnbStandardFeeType,
  AirbnbMobileOnlyDiscountStatus,
  AirbnbTopRatedGuestDiscountPromotionType,
  AirbnbTopRatedGuestDiscountStatus,
  airbnbCurrencies,
  airbnbMonthlyPricingRuleDays,
  airbnbOccupancyTaxAmountTypes,
  airbnbOccupancyTaxBase,
  airbnbOccupancyTaxCollectionType,
  airbnbOccupancyTaxTypes,
  airbnbOccupancyTaxesAttestation,
  airbnbWeeklyPricingRuleDays,
  airbnbSeasonalOccupancyTaxLabel,
  getAirbnbBlackoutDatesYearQuotas,
  isAirbnbSeasonalOccupancyTax,
  toAirbnbBlackoutDateRanges,
} from '@tokeet-frontend/channels'
import { AirbnbBlackoutDatesDialogService } from '../airbnb-blackout-dates-dialog/airbnb-blackout-dates-dialog.service'
import { Observable } from 'rxjs'

function requiredDep(ctrl: AbstractControl, depCtrl: AbstractControl, isRequired: (value: any) => boolean) {
  depCtrl.valueChanges
    .pipe(
      startWith(depCtrl.value),
      filter((v) => !lodash.isNil(v))
    )
    .subscribe((v) => {
      if (isRequired(v)) {
        ctrl.setValidators([Validators.required])
      } else {
        ctrl.clearValidators()
      }
      ctrl.updateValueAndValidity()
      ctrl.markAsTouched()
    })
}

function percentFlatDep(ctrl: AbstractControl, depCtrl: AbstractControl, isPercent: (value: any) => boolean) {
  depCtrl.valueChanges
    .pipe(
      startWith(depCtrl.value),
      filter((v) => !lodash.isNil(v))
    )
    .subscribe((v) => {
      if (isPercent(v)) {
        ctrl.setValidators([Validators.min(0), Validators.max(100)])
      } else {
        ctrl.setValidators([Validators.min(0)])
      }
      ctrl.updateValueAndValidity()
      ctrl.markAsTouched()
    })
}

// #region Airbnb Standard fee
function createStandardFeesForm(fb: FormBuilder) {
  const feeTypes = Object.values(AirbnbStandardFeeType)
  const feesForm = fb.group({})
  feeTypes.forEach((feeType) => {
    const form = fb.group({
      fee_type: [feeType], // hidden
      amount_type: ['flat'], // hidden
      amount: [],
      charge_type: ['PER_GROUP'], // hidden
    })
    percentFlatDep(form.get('amount'), form.get('amount_type'), (v) => v === 'percent')
    feesForm.addControl(feeType, form)
  })

  return feesForm
}

function setStandardFeesForm(standardFeesForm: FormGroup, standardFees: AirbnbPricingStandardFee[]) {
  lodash.forEach(standardFees, (fee) => {
    const feeForm = standardFeesForm.get(fee.fee_type) as FormGroup
    if (!feeForm) return
    feeForm.patchValue(fromAirbnbStandardFee(fee))
  })
}

function fromAirbnbStandardFee(fee: AirbnbPricingStandardFee): AirbnbPricingStandardFee {
  return {
    ...fee,
    amount: fee.amount_type === 'flat' ? fee.amount / 1000000 : fee.amount,
  }
}

function toAirbnbStandardFee(fee: AirbnbPricingStandardFee): AirbnbPricingStandardFee {
  return {
    ...fee,
    amount: fee.amount_type === 'flat' ? fee.amount * 1000000 : fee.amount,
  }
}
// #endregion

// #region Airbnb Pricing rule

function createPricingRuleForm(fb: FormBuilder, ruleType: AirbnbPricingRuleType, rule?: AirbnbPricingRule) {
  const getThresholdValidators = (ruleType: AirbnbPricingRuleType) => {
    switch (ruleType) {
      case AirbnbPricingRuleType.lastMinutes:
        return [Validators.min(1), Validators.max(28)]
      case AirbnbPricingRuleType.earlyBird:
        return [Validators.min(1), Validators.max(36)]
      default:
        return []
    }
  }

  const form = fb.group({
    rule_type: [ruleType], // hidden
    threshold_one: [undefined, getThresholdValidators(ruleType)],
    price_change: [undefined, [Validators.min(0), Validators.max(100)]],
    price_change_type: ['PERCENT'], // hidden
  })

  if (rule) {
    form.patchValue(fromAirbnbPricingRule(rule))
  }

  return form
}

function createInitPricingRulesForm(fb: FormBuilder) {
  const ruleTypes = lodash.values(AirbnbPricingRuleType)
  const rulesForm = fb.group({})

  lodash.forEach(ruleTypes, (ruleType: AirbnbPricingRuleType) => {
    const array = fb.array([createPricingRuleForm(fb, ruleType)])
    rulesForm.addControl(ruleType, array)
  })

  return rulesForm
}

function setPricingRulesForm(fb: FormBuilder, rulesForm: FormGroup, rules: AirbnbPricingRule[]) {
  const rulesByType = lodash.groupBy(rules, (r) => r.rule_type)
  const ruleTypes = lodash.values(AirbnbPricingRuleType)

  lodash.forEach(rulesByType, (items: AirbnbPricingRule[], ruleType: AirbnbPricingRuleType) => {
    if (!ruleTypes.includes(ruleType)) return

    const array = fb.array([])
    items.forEach((item) => array.push(createPricingRuleForm(fb, item.rule_type, item)))
    rulesForm.setControl(ruleType, array)
  })

  return rulesForm
}

function fromAirbnbPricingRule(rule: AirbnbPricingRule): AirbnbPricingRule {
  let threshold_one = rule.threshold_one
  if (rule.rule_type === AirbnbPricingRuleType.earlyBird) {
    threshold_one = Math.round(threshold_one / 30) // month
  }
  return {
    ...rule,
    threshold_one,
    price_change: -rule.price_change,
  }
}

function toAirbnbPricingRule(rule: AirbnbPricingRule): AirbnbPricingRule {
  let threshold_one = rule.threshold_one
  if (rule.rule_type === AirbnbPricingRuleType.earlyBird) {
    threshold_one = threshold_one * 30 // month
  }
  return {
    ...rule,
    threshold_one,
    price_change: -rule.price_change,
  }
}
// #endregion

// #region Airbnb Occupancy Tax
function createAirbnbOccupancyTaxForm(fb: FormBuilder, item?: AirbnbPassThroughOccupancyTax) {
  const form = fb.group({
    tax_type: [item?.tax_type, [Validators.required]],
    amount_type: [item?.amount_type, [Validators.required]],
    amount: [item?.amount, [Validators.required]],
    taxable_base: [item?.taxable_base || []],
    attestation: [true],

    business_tax_id: [item?.business_tax_id, [Validators.required]],
    no_business_tax_id_declaration: [item?.no_business_tax_id_declaration],

    tot_registration_id: [item?.tot_registration_id, [Validators.required]],
    no_tot_registration_id_declaration: [item?.no_tot_registration_id_declaration],

    long_term_stay_exemption: [item?.long_term_stay_exemption, [Validators.min(0), integer()]],
    only_first_nights_exemption: [item?.only_first_nights_exemption, [Validators.min(0), integer()]],
    max_cap_per_person_per_night: [item?.max_cap_per_person_per_night, [Validators.min(0), integer()]],
  })

  percentFlatDep(form.get('amount'), form.get('amount_type'), (v) => v === 'percent_per_reservation')
  requiredDep(form.get('taxable_base'), form.get('amount_type'), (v) => v === 'percent_per_reservation')
  requiredDep(form.get('business_tax_id'), form.get('no_business_tax_id_declaration'), (v) => !v)
  // requiredDep(form.get('tot_registration_id'), form.get('no_tot_registration_id_declaration'), (v) => !v)

  return form
}

function createAirbnbOccupancyTaxesForm(
  fb: FormBuilder,
  taxesForm: FormArray,
  occupancyTaxes: AirbnbPassThroughOccupancyTax[]
) {
  taxesForm.clear()
  lodash
    .map(occupancyTaxes, (item) => createAirbnbOccupancyTaxForm(fb, item))
    .forEach((item) => {
      taxesForm.push(item)
    })
}

function toAirbnbOccupancyTaxes(occupancyTaxes: AirbnbPassThroughOccupancyTax[]): AirbnbPassThroughOccupancyTax[] {
  return lodash.map(occupancyTaxes, (tax) => {
    if (lodash.isEmpty(tax.taxable_base)) {
      tax.taxable_base = ['base_price']
    }
    return tax
  })
}

// #endregion

@Component({
  selector: 'app-airbnb-update-price-settings-dialog',
  templateUrl: './airbnb-update-price-settings-dialog.component.html',
  styleUrls: ['./airbnb-update-price-settings-dialog.component.scss'],
})
export class AirbnbUpdatePriceSettingsDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    default_daily_price: [undefined],
    listing_currency: [undefined, [Validators.required]],
    weekly_price_factor: [{ value: null, disabled: true }],
    monthly_price_factor: [{ value: null, disabled: true }],
    weekend_price: [undefined],
    guests_included: [undefined],
    price_per_extra_person: [undefined],

    standard_fees: createStandardFeesForm(this.fb),
    default_pricing_rules: createInitPricingRulesForm(this.fb),

    pass_through_taxes: this.fb.array([]),
  })

  get standardFeesForm() {
    return this.form?.get('standard_fees') as FormGroup
  }
  get pricingRulesForm() {
    return this.form?.get('default_pricing_rules') as FormGroup
  }

  get occupancyTaxesForm() {
    return this.form?.get('pass_through_taxes') as FormArray
  }

  standardFeeTypes = AirbnbStandardFeeType
  pricingRuleTypes = AirbnbPricingRuleType

  airbnbOccupancyTaxesTypes = airbnbOccupancyTaxTypes
  airbnbOccupancyTaxAmountTypes = airbnbOccupancyTaxAmountTypes
  airbnbOccupancyTaxBase = airbnbOccupancyTaxBase
  airbnbOccupancyTaxesAttestation = airbnbOccupancyTaxesAttestation
  occupancyTaxNotice: string
  // TV4-6258: seasonal taxes are read-only here and are sent back unchanged.
  seasonalOccupancyTaxes: AirbnbPassThroughOccupancyTax[] = []
  seasonalOccupancyTaxLabel = airbnbSeasonalOccupancyTaxLabel

  rental: Rental

  currencyOptions = airbnbCurrencies
  currency = 'USD'
  isPetsFeeAllowed = false

  promotionStatus: AirbnbPromotionStatus
  promotionStatuses = AirbnbPromotionStatus
  topRatedGuestDiscount: AirbnbTopRatedGuestDiscountStatus
  topRatedGuestDiscountLoading = false
  mobileOnlyDiscount: AirbnbMobileOnlyDiscountStatus
  mobileOnlyDiscountLoading = false

  toBlackoutDateRanges = toAirbnbBlackoutDateRanges
  getBlackoutDatesYearQuotas = getAirbnbBlackoutDatesYearQuotas

  constructor(
    public dialogRef: MatDialogRef<AirbnbUpdatePriceSettingsDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private store: Store<fromRoot.State>,
    private connectionAirbnbService: ConnectionAirbnbService,
    private airbnbService: AirbnbChannelService,
    private confirm: ConfirmDialogService,
    private airbnbBlackoutDatesDialog: AirbnbBlackoutDatesDialogService,
    @Inject(MAT_DIALOG_DATA) public data: { connection: Connection }
  ) {
    super()
  }

  ngOnInit() {
    const connection = this.data.connection

    this.pricingRulesForm.valueChanges
      .pipe(startWith(this.pricingRulesForm.value), untilDestroy(this))
      .subscribe((rules) => {
        const longTermRules: AirbnbPricingRule[] = rules[AirbnbPricingRuleType.longTerm] || []
        this.form.patchValue(
          {
            weekly_price_factor:
              longTermRules.find((rule) => rule.threshold_one === airbnbWeeklyPricingRuleDays)?.price_change ?? null,
            monthly_price_factor:
              longTermRules.find((rule) => rule.threshold_one === airbnbMonthlyPricingRuleDays)?.price_change ?? null,
          },
          { emitEvent: false }
        )
      })

    this.store.pipe(select(selectRentalById(connection.rentalId)), untilDestroy(this)).subscribe((rental) => {
      this.rental = rental
    })

    combineLatest([
      this.store.pipe(
        select(selectChannelById, { id: getRealChannelId(this.data.connection.channelId) }),
        switchMap((channel) =>
          this.connectionAirbnbService.getPriceSettingsV2(
            this.data.connection.channelId,
            // @ts-ignore
            this.data.connection.propertyId,
            this.data.connection.roomId
          )
        ),
        take(1)
      ),
      this.connectionAirbnbService.getListingDetailsV2(connection.propertyId, connection.roomId),
    ]).subscribe(([pricing_setting, details]: [AirBnBPriceSettingsResponse, AibrnbListingDetails]) => {
      setStandardFeesForm(this.standardFeesForm, pricing_setting.standard_fees)
      setPricingRulesForm(this.fb, this.pricingRulesForm, pricing_setting.default_pricing_rules)
      const [seasonalTaxes, taxes] = lodash.partition(pricing_setting.pass_through_taxes, isAirbnbSeasonalOccupancyTax)
      this.seasonalOccupancyTaxes = seasonalTaxes
      createAirbnbOccupancyTaxesForm(this.fb, this.occupancyTaxesForm, taxes)
      this.occupancyTaxNotice = lodash.find(
        airbnbOccupancyTaxCollectionType,
        (t) => t.id === pricing_setting.pass_through_taxes_collection_type
      )?.description

      this.isPetsFeeAllowed = !!details.pets_allowed
      if (!this.isPetsFeeAllowed) {
        this.standardFeesForm.get(AirbnbStandardFeeType.petFee).disable()
      }

      this.form.patchValue({
        default_daily_price: pricing_setting.default_daily_price,
        weekend_price: pricing_setting.weekend_price,
        guests_included: pricing_setting.guests_included,
        price_per_extra_person: pricing_setting.price_per_extra_person,
        listing_currency: pricing_setting.listing_currency,
      })
    })

    this.airbnbService
      .getListingPromotions(connection.propertyId, connection.channelId, [connection.roomId])
      .pipe(map((items) => lodash.head(items)))
      .subscribe((res) => {
        this.promotionStatus = res?.status
      })

    this.loadListingDiscounts()

    this.form
      .get('listing_currency')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((c) => {
        this.currency = c
      })
  }

  loadListingDiscounts() {
    this.airbnbService
      .getListingDiscounts(this.data.connection.propertyId, this.data.connection.channelId, this.data.connection.roomId)
      .subscribe(
        (res) => {
          this.topRatedGuestDiscount = res?.top_rated_guest_discount
          this.mobileOnlyDiscount = res?.mobile_only_discount
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  onPromotionStatusChange(value: boolean) {
    if (value) {
      this.airbnbService
        .createListingPromotion(this.data.connection.propertyId, this.data.connection.channelId, [
          this.data.connection.roomId,
        ])
        .subscribe(
          () => {
            this.promotionStatus = this.promotionStatuses.ONGOING
          },
          (error) => this.store.dispatch(ActionFailed({ error }))
        )
    } else {
      this.airbnbService
        .deleteListingPromotion(this.data.connection.propertyId, this.data.connection.channelId, [
          this.data.connection.roomId,
        ])
        .subscribe(
          () => {
            this.promotionStatus = this.promotionStatuses.AVAILABLE
          },
          (error) => this.store.dispatch(ActionFailed({ error }))
        )
    }
  }

  onTopRatedGuestDiscountChange(value: boolean) {
    this.topRatedGuestDiscountLoading = true
    this.airbnbService
      .setTopRatedGuestDiscount(
        this.data.connection.propertyId,
        this.data.connection.channelId,
        this.data.connection.roomId,
        value
      )
      .subscribe(
        (res) => {
          this.topRatedGuestDiscount = res
          this.topRatedGuestDiscountLoading = false
        },
        (error) => {
          this.topRatedGuestDiscountLoading = false
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onMobileOnlyDiscountChange(value: boolean) {
    this.mobileOnlyDiscountLoading = true
    this.airbnbService
      .setMobileOnlyDiscount(
        this.data.connection.propertyId,
        this.data.connection.channelId,
        this.data.connection.roomId,
        value
      )
      .subscribe(
        (res) => {
          this.mobileOnlyDiscount = res
          this.mobileOnlyDiscountLoading = false
        },
        (error) => {
          this.mobileOnlyDiscountLoading = false
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  openBlackoutDates(discount: AirbnbListingPromotionProgramStatus) {
    this.airbnbBlackoutDatesDialog.open(this.data.connection, discount).subscribe((res) => {
      if (res.type === AirbnbTopRatedGuestDiscountPromotionType) {
        this.topRatedGuestDiscount = res as AirbnbTopRatedGuestDiscountStatus
      } else {
        this.mobileOnlyDiscount = res as AirbnbMobileOnlyDiscountStatus
      }
    })
  }

  addPricingRule(ruleType: AirbnbPricingRuleType) {
    const array = this.pricingRulesForm.get(ruleType) as FormArray
    array.push(createPricingRuleForm(this.fb, ruleType))
  }

  removePricingRule(ruleType: AirbnbPricingRuleType, index: number) {
    const array = this.pricingRulesForm.get(ruleType) as FormArray
    array.removeAt(index)
  }

  addTax() {
    this.occupancyTaxesForm.push(createAirbnbOccupancyTaxForm(this.fb))
  }

  removeTax(index: number) {
    this.occupancyTaxesForm.removeAt(index)
  }

  close() {
    this.dialogRef.close()
  }
  onSave() {
    this.save(this.form)
    if (this.form.invalid) {
      this.toaster.warning(
        $localize`Please make sure all fields are filled correctly.`,
        $localize`Unable to save settings`
      )
    }
  }

  @SaveForm()
  save(form: FormGroup) {
    const {
      default_daily_price,
      weekend_price,
      // cleaning_fee,
      // security_deposit,
      guests_included,
      price_per_extra_person,
      listing_currency,
      standard_fees: standardFees,
      default_pricing_rules: pricingRules,
      pass_through_taxes: occupancyTaxes,
    } = this.form.getRawValue()

    const standard_fees = lodash
      .values(standardFees)
      .filter((fee: AirbnbPricingStandardFee) =>
        !this.isPetsFeeAllowed ? fee.fee_type !== AirbnbStandardFeeType.petFee : true
      )
      .filter((fee) => lodash.isNumber(fee.amount))
      .map((fee) => ({
        ...toAirbnbStandardFee(fee),
        // Airbnb accepts only a security deposit that the host collects (offline).
        offline: fee.fee_type === AirbnbStandardFeeType.securityDeposit,
      }))

    const default_pricing_rules = lodash
      .flatten(lodash.values(pricingRules))
      .filter((rule: AirbnbPricingRule) => lodash.isNumber(rule.price_change) && lodash.isNumber(rule.threshold_one))
      .map((fee) => toAirbnbPricingRule(fee))

    const payload: AirBnBUpdatePriceSettingsPayload = {
      channelId: this.data.connection.channelId,
      // @ts-ignore
      propertyId: this.data.connection.propertyId,
      // @ts-ignore
      roomid: this.data.connection.roomId,
      // @ts-ignore
      listing_id: this.data.connection.roomId,
      default_daily_price,
      guests_included,
      price_per_extra_person,
      weekend_price,
      listing_currency,
      standard_fees,
      default_pricing_rules,
      pass_through_taxes: [...toAirbnbOccupancyTaxes(occupancyTaxes), ...this.seasonalOccupancyTaxes],
    }

    let saveData$: Observable<any>

    if (!lodash.isEmpty(occupancyTaxes)) {
      saveData$ = this.confirm
        .confirmExtra({
          title: $localize`Terms for adding taxes`,
          body: $localize`I confirm this tax amount is correct
        and I will pay this tax once remitted to me by Airbnb.
        I grant Airbnb permission to disclose tax-related and transaction
        information (such as listing address, tax amount, and Tax ID) to
        the relevant tax authorities.`,
          extra: $localize`I Agree to terms for adding taxes`,
          confirmText: $localize`Save`,
          cancelText: $localize`Cancel`,
        })
        .pipe(
          tap(({ isChecked }) => {
            if (!isChecked) {
              this.toaster.warning($localize`Please agree to the terms for adding taxes.`)
            }
          }),
          filter(({ isChecked }) => isChecked),
          switchMap(() => this.connectionAirbnbService.updatePriceSettingsV2(payload))
        )
    } else {
      saveData$ = this.connectionAirbnbService.updatePriceSettingsV2(payload)
    }
    saveData$.subscribe(
      () => {
        this.toaster.success($localize`Price settings updated successfully.`)
        this.dialogRef.close()
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }
}
